import {
  mapImage,
  mapText,
  mapTitle,
  pickStr,
  mapOverlayDirection,
  mapOverlayOpacity,
  normalizeSpacer,
} from "@/components/DesignSystem/utils/teaserHelper"
import { validateLargeImageAndTextBlockProps } from "../validators/largeImageAndTextValidator"

/**
 * Maps an AEM "large image and text block" container into KDS props.
 *
 * Maps container data to ILargeImageAndTextBlockCombinedProps interface:
 * - `mod` - Theme modification (white, gray-100, gray-200, etc.)
 * - `variant` - Display variant (text-over-image, full-bleed-text-over-image, text-and-image-blocks)
 * - `textPlacement` - Text positioning (center, left)
 * - `image` - Image component properties (required)
 * - `showHeader` - Whether to show header section
 * - `heading` - Title/heading component properties
 * - `copy` - Body text component properties
 * - `CTAs` - Array of call-to-action links
 * - `gradientDirection` - Photo overlay gradient direction
 * - `opacity` - Photo overlay opacity level
 * - `topSpacer` - Spacer properties for top spacing
 * - `bottomSpacer` - Spacer properties for bottom spacing
 *
 * @param {Record<string, any>} container Raw AEM container node.
 * @return {Record<string, any> | null} Mapped props object or `null` if nothing to map.
 */
export function mapLargeImageAndTextProps(container) {
  try {
    const itemsOrder = container[":itemsOrder"]
    if (!itemsOrder || !Array.isArray(itemsOrder) || itemsOrder.length === 0) {
      return null
    }

    const firstItemKey = itemsOrder[0]
    const blockItem = container[":items"]?.[firstItemKey]

    if (!blockItem) return null

    // Validate and extract mod (must be one of the allowed values)
    const validMods = new Set([
      "white",
      "gray-100",
      "gray-200",
      "gray-500",
      "slate",
      "charcoal",
      "black",
      "gray-light",
      "light-beige",
    ])
    const modValue = pickStr(blockItem.mod)
    const mod = modValue && validMods.has(modValue) ? modValue : undefined

    // Validate and extract variant (must be one of the allowed values)
    const validVariants = new Set([
      "text-over-image",
      "full-bleed-text-over-image",
      "text-and-image-blocks",
    ])
    const variantValue = pickStr(blockItem.variant)
    const variant =
      variantValue && validVariants.has(variantValue)
        ? variantValue
        : "text-over-image"

    // Validate and extract text placement (center or left only)
    const validPlacements = new Set(["center", "left"])
    const placementValue = pickStr(blockItem.textPlacement)
    const textPlacement =
      placementValue && validPlacements.has(placementValue)
        ? placementValue
        : "center"

    // Extract show header flag (defaults to true)
    const showHeader = blockItem.showHeader !== false

    // Extract heading - handle both array and single object
    const headingData = Array.isArray(blockItem.heading)
      ? blockItem.heading[0]
      : blockItem.heading
    const heading = mapTitle(headingData)

    // Extract copy - handle both array and single object
    const copyData = Array.isArray(blockItem.copy)
      ? blockItem.copy[0]
      : blockItem.copy
    const copy = mapText(copyData)

    // Extract image - handle both array and single object (REQUIRED)
    const imageData = Array.isArray(blockItem.image)
      ? blockItem.image[0]
      : blockItem.image
    const image = mapImage(imageData)

    // Image is required - return null if not provided
    if (!image || !image.src) {
      return null
    }

    // Ensure image has required alt property
    if (!image.alt) {
      image.alt = ""
    }

    // Extract gradient direction
    const gradientDirection = mapOverlayDirection(blockItem.gradientDirection)

    // Extract opacity
    const opacity = mapOverlayOpacity(blockItem.opacity)

    // Extract spacers
    const topSpacer = normalizeSpacer(blockItem.topSpacer)
    const bottomSpacer = normalizeSpacer(blockItem.bottomSpacer)

    // Extract CTAs - handle both array and single object
    const CTAs = []
    const ctaData = blockItem.CTAs || blockItem.ctas || blockItem.actions
    if (ctaData && Array.isArray(ctaData)) {
      ctaData.forEach(cta => {
        if (cta && (cta.href || cta.url || cta.link)) {
          CTAs.push({
            text: cta.text || cta.title || "",
            href: cta.href || cta.url || cta.link,
          })
        }
      })
    }

    const largeImageAndTextProps = {
      image,
      variant,
      textPlacement,
    }

    if (mod) largeImageAndTextProps.mod = mod
    if (showHeader !== undefined && showHeader !== true)
      largeImageAndTextProps.showHeader = showHeader
    if (heading) largeImageAndTextProps.heading = heading
    if (copy) largeImageAndTextProps.copy = copy
    if (CTAs.length > 0) largeImageAndTextProps.CTAs = CTAs
    if (gradientDirection)
      largeImageAndTextProps.gradientDirection = gradientDirection
    if (opacity !== undefined && opacity !== 35)
      largeImageAndTextProps.opacity = opacity
    if (topSpacer && topSpacer.size && topSpacer.size !== "none")
      largeImageAndTextProps.topSpacer = topSpacer
    if (bottomSpacer && bottomSpacer.size && bottomSpacer.size !== "none")
      largeImageAndTextProps.bottomSpacer = bottomSpacer

    const { isValid, errors } = validateLargeImageAndTextBlockProps(
      largeImageAndTextProps
    )

    if (!isValid) {
      console.warn("largeImageAndTextProps validation errors:", errors)
      return null
    }

    return largeImageAndTextProps
  } catch (error) {
    console.error("Error mapping LargeImageAndText props:", error)
    return null
  }
}
